import { findImportTargetField } from "@lafayette/import-mapping";
import type { StaffImportProposalV2, StaffImportReviewRowDetail } from "@lafayette/schemas";

import { itemTypeLabels, priceUnitLabels, schedulePrecisionLabels } from "../../catalog/labels";
import { minorToMajor } from "../../catalog/money";
import { columnLabel } from "../columns";

// How the drawer shows a row: the raw cells under their headers, and the proposal as labelled lines.
// Both are pure functions of the R6 answer. The text of a cell or a proposed name goes to the page as
// text only. Nothing here builds a toast, a log line or an error message.

export const targetFieldLabel = (code: string): string =>
  findImportTargetField(code)?.label ?? code.replaceAll("_", " ");

export type RawCell = Readonly<{ label: string; value: string }>;

/** The raw cells of a row under the column label of the header ("Column C · eventName"). */
export function rawCells(
  detail: Pick<StaffImportReviewRowDetail, "headers" | "rawCells">,
): RawCell[] {
  const count = Math.max(detail.headers.length, detail.rawCells.length);
  return Array.from({ length: count }, (_, index) => ({
    label: columnLabel({ index, header: detail.headers[index] ?? "" }),
    value: detail.rawCells[index] ?? "",
  }));
}

export type ProposalLine = Readonly<{
  key: string;
  label: string;
  /** One or more display values (an ordered list for people and tags). */
  values: readonly string[];
  /** A short sentence under the value, for example where a time zone came from. `null` for none. */
  note: string | null;
}>;

const line = (
  key: string,
  label: string,
  values: readonly string[],
  note: string | null = null,
): ProposalLine => ({ key, label, values, note });

const text = (value: string | null): string | null =>
  value === null || value.trim() === "" ? null : value;

function formatInstant(instant: string, timeZone: string, dateOnly: boolean): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  const options: Intl.DateTimeFormatOptions = dateOnly
    ? { dateStyle: "medium" }
    : { dateStyle: "medium", timeStyle: "short" };
  try {
    return new Intl.DateTimeFormat("en-GB", { ...options, timeZone }).format(date);
  } catch {
    return `${instant} (UTC)`;
  }
}

/** The price of a proposal in words. A missing currency is said, never guessed. */
export function describePrice(price: NonNullable<StaffImportProposalV2["price"]>): string {
  const money = (minor: number | null): string =>
    minor === null ? "?" : minorToMajor(minor, price.currency ?? "USD");
  const currency = price.currency ?? "currency not set";
  const unit = price.unit === null ? "" : `, ${priceUnitLabels[price.unit].toLowerCase()}`;
  const terms = price.terms === null ? "" : ` (${price.terms})`;
  switch (price.model) {
    case "FIXED":
      return `${money(price.amountMinor)} ${currency}${unit}${terms}`;
    case "FROM":
      return `From ${money(price.minAmountMinor ?? price.amountMinor)} ${currency}${unit}${terms}`;
    case "RANGE":
      return `${money(price.minAmountMinor)} to ${money(price.maxAmountMinor)} ${currency}${unit}${terms}`;
    case "QUOTE_REQUIRED":
      return `Price on request${terms}`;
    case "UNKNOWN":
      return price.terms === null ? "No price" : price.terms;
  }
}

const zoneNotes = {
  COLUMN: "The time zone is in the file.",
  JOB_DEFAULT: "The time zone is the default of this import.",
  CITY_LOOKUP: "The time zone was guessed from the city. It needs your confirmation.",
} as const;

/** The proposal as labelled lines, in a fixed order. An empty value gets no line. */
export function proposalLines(proposal: StaffImportProposalV2): ProposalLine[] {
  const lines: ProposalLine[] = [
    line("itemType", "Item type", [itemTypeLabels[proposal.itemType]]),
    line("name", "Name", [proposal.name]),
  ];
  const simple: readonly (readonly [string, string, string | null])[] = [
    ["shortDescription", "Short description", proposal.shortDescription],
    ["description", "Description", proposal.description],
    ["addressText", "Address", proposal.addressText],
    ["locality", "City", proposal.locality],
    ["region", "Region", proposal.region],
    ["countryCode", "Country", proposal.countryCode],
    ["externalUrl", "Website", proposal.externalUrl],
    ["placeId", "Google Place ID", proposal.placeId ?? null],
    ["category", "Category", proposal.category],
  ];
  for (const [key, label, value] of simple) {
    const shown = text(value);
    if (shown !== null) lines.push(line(key, label, [shown]));
  }
  if (proposal.latitude !== null && proposal.longitude !== null) {
    lines.push(line("coordinates", "Coordinates", [`${proposal.latitude}, ${proposal.longitude}`]));
  }
  if (proposal.schedule !== null) {
    const { schedule } = proposal;
    const dateOnly = schedule.precision === "DATE_ONLY";
    lines.push(
      line(
        "startsAt",
        "Starts",
        [formatInstant(schedule.startsAt, schedule.timeZone, dateOnly)],
        `${schedulePrecisionLabels[schedule.precision]}, ${schedule.timeZone}. UTC ${schedule.startsAt}.`,
      ),
    );
    if (schedule.endsAt !== null) {
      lines.push(
        line("endsAt", "Ends", [formatInstant(schedule.endsAt, schedule.timeZone, dateOnly)]),
      );
    }
    lines.push(line("timeZone", "Time zone", [schedule.timeZone], zoneNotes[schedule.zoneSource]));
  } else if (proposal.timeZone !== null) {
    lines.push(line("timeZone", "Time zone", [proposal.timeZone]));
  }
  if (proposal.price !== null) {
    lines.push(
      line(
        "price",
        "Price",
        [describePrice(proposal.price)],
        proposal.price.currencyState === "AMBIGUOUS"
          ? "The source does not say which currency this is."
          : null,
      ),
    );
  }
  if (proposal.tags.length > 0) {
    lines.push(
      line(
        "tags",
        "Tags",
        proposal.tags.map((tag) => `${tag.namespace}: ${tag.code}`),
      ),
    );
  }
  for (const [key, value] of Object.entries(proposal.subtype)) {
    lines.push(line(`subtype.${key}`, targetFieldLabel(key), [String(value)]));
  }
  if (proposal.people.length > 0) {
    lines.push(
      line(
        "people",
        "People",
        [...proposal.people].sort((a, b) => a.position - b.position).map((p) => p.displayName),
        "In this order.",
      ),
    );
  }
  lines.push(
    line(
      "source",
      "Source",
      [`${proposal.source.system} · ${proposal.source.key}`],
      proposal.source.keyDerived
        ? "The key was made from the content of the row, because no source ID column is mapped."
        : null,
    ),
  );
  return lines;
}

const ruleLabels = {
  CLEANED: "Tidied (spaces and case)",
  PARSED: "Read from the text",
  DATE_PARTS_JOINED: "Day, month, year and time joined into one moment",
  CODE_MATCH: "Matched a known code",
  LABEL_MATCH: "Matched a known name",
  SYNONYM: "Matched a known synonym",
  MODEL_ASSISTED: "Suggested by a model",
  CURRENCY_CODE: "Currency from a currency code",
  CURRENCY_SYMBOL: "Currency from a symbol",
  CURRENCY_COLUMN: "Currency from a column",
  SPLIT_LIST: "Split into a list",
  COUNTRY_LOOKUP: "Country found by name",
  URL_NORMALIZED: "Link tidied",
  DEFAULT: "Default value",
} as const;

const fieldSourceLabels = {
  CELL: "one cell",
  CELLS: "several cells",
  JOB_SETTING: "the settings of this import",
  CITY_LOOKUP: "the city",
  MODEL: "a model",
  ITEM_TYPE_DEFAULT: "the default for the item type",
} as const;

/** One sentence per normalization rule the validator applied, by field. */
export function ruleLines(proposal: StaffImportProposalV2): readonly Readonly<{
  key: string;
  label: string;
  text: string;
}>[] {
  return Object.entries(proposal.fieldRules).map(([key, value]) => ({
    key,
    label: targetFieldLabel(key),
    text: `${ruleLabels[value.rule]}, from ${fieldSourceLabels[value.source]}.`,
  }));
}

/** The proposed amount fields of a price, for the currency decision. */
export function describeAmounts(price: NonNullable<StaffImportProposalV2["price"]>): string {
  return describePrice({ ...price, currency: null });
}

/** A confidence in basis points as a percent: 9300 is "93%". */
export const percent = (basisPoints: number): string => `${Math.round(basisPoints / 100)}%`;
